import {Component, Input, OnInit} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Password} from 'primeng/password';
import {Select} from 'primeng/select';
import {FloatLabel} from 'primeng/floatlabel';
import {ToggleButton} from 'primeng/togglebutton';
import {MAILBOX_PROVIDERS_OPTIONS, MailboxProvider} from '../../mailbox.model';

@Component({
	selector: 'app-outbound-form',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		InputText,
		Password,
		Select,
		FloatLabel,
		ToggleButton
	],
	template: `
		<form [formGroup]="form" class="p-fluid">
			<div class="flex flex-column gap-3">

				<!-- PROVIDER -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<p-select
							[options]="MAILBOX_PROVIDERS_OPTIONS"
							formControlName="client"
							optionLabel="label"
							optionValue="value"
							fluid/>
						<label for="client">Provider</label>
					</p-floatlabel>
					<small class="text-500">Seleziona il provider SMTP</small>
				</div>

				<!-- EMAIL -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText formControlName="email" fluid/>
						<label for="email">Email</label>
					</p-floatlabel>
					<small class="text-500">Indirizzo email utilizzato per l’invio</small>
				</div>

				<!-- USERNAME -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText
							   formControlName="username"
							   autocomplete="off"
							   fluid />
						<label for="username">Username</label>
					</p-floatlabel>
					<small class="text-500">Spesso coincide con l’email</small>
				</div>

				<!-- PASSWORD -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<p-password formControlName="password"
									[toggleMask]="true"
									[feedback]="false"
									autocomplete="new-password"
									fluid />
						<label for="password">Password</label>
					</p-floatlabel>
					<small class="text-500">Password o password applicativa SMTP</small>
				</div>

				<!-- ALIAS -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText formControlName="email_configuration_alias" fluid/>
						<label for="email_configuration_alias">Alias</label>
					</p-floatlabel>
					<small class="text-500">Nome identificativo della configurazione</small>
				</div>

				<!-- FROM NAME -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText formControlName="from_name" fluid/>
						<label for="from_name">From Name</label>
					</p-floatlabel>
					<small class="text-500">Nome visualizzato dal destinatario</small>
				</div>

				<!-- FROM ADDRESS -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText formControlName="from_address" fluid/>
						<label for="from_address">From Address (opzionale)</label>
					</p-floatlabel>
					<small class="text-500">Email mittente (se diversa dalla principale)</small>
				</div>

				<!-- HOST -->
				@if (isHostVisible()) {
					<div class="flex flex-column">
						<p-floatlabel variant="on">
							<input pInputText formControlName="host" fluid/>
							<label for="host">Host</label>
						</p-floatlabel>
						<small class="text-500">Server SMTP (es. smtp.gmail.com)</small>
					</div>
				}

				<!-- CUSTOM -->
				@if (isCustom()) {
					<div class="flex flex-column">
						<p-floatlabel variant="on">
							<input pInputText formControlName="smtp_port" fluid/>
							<label for="smtp_port">SMTP Port</label>
						</p-floatlabel>
						<small class="text-500">Porta SMTP (es. 465 SSL o 587 TLS)</small>
					</div>

					<div>
						<p-toggleButton
							formControlName="starttls_enable"
							onLabel="StartTLS"
							offLabel="StartTLS"/>
						<small class="text-500">Connessione sicura tramite TLS</small>
					</div>

					<div>
						<p-toggleButton
							formControlName="email_auth"
							onLabel="Auth"
							offLabel="Auth"/>
						<small class="text-500">Richiede autenticazione per inviare email</small>
					</div>
				}

			</div>
		</form>
	`
})
export class OutboundFormComponent implements OnInit {
	@Input({ required: true }) form!: FormGroup;

	protected readonly MAILBOX_PROVIDERS_OPTIONS = MAILBOX_PROVIDERS_OPTIONS;

	client: MailboxProvider | null = null;

	ngOnInit() {
		this.client = this.form.get('client')?.value;

		this.form.get('client')?.valueChanges.subscribe(client => {
			this.client = client as MailboxProvider;
			this.applyValidation(this.client);
		});
	}

	isHostVisible() {
		return this.client === 'CUSTOM' || this.client === 'AWS_WORKMAIL';
	}

	isCustom() {
		return this.client === 'CUSTOM';
	}

	private applyValidation(provider: MailboxProvider | null) {
		const host = this.form.get('host');
		const smtp = this.form.get('smtp_port');

		host?.clearValidators();
		smtp?.clearValidators();

		if (provider === 'AWS_WORKMAIL') {
			host?.setValidators([]);
		}

		if (provider === 'CUSTOM') {
			host?.setValidators([]);
			smtp?.setValidators([]);
		}

		host?.updateValueAndValidity();
		smtp?.updateValueAndValidity();
	}
}
